/**
 * Global Error Handling Service
 *
 * Sets up global error handlers for window.onerror and unhandledrejection
 * to complement PostHog's autocapture functionality.
 */

import { logger, unknownToLogString, getPostHogInstance } from '@repo/shared-utils';
import { trimTrailingSlashes } from '../utils/strings';
import {
  allowRawStacksInClientTelemetry,
  sanitizeForClientTelemetry,
  summarizeStack,
} from './clientTelemetrySanitize';

let isInitialized = false;

function getTelemetryEndpoint(): string {
  let origin: string | undefined;
  try {
    if (typeof import.meta !== 'undefined') {
      const env = (import.meta as { env?: Record<string, string> }).env;
      const raw = env?.VITE_API_ORIGIN?.trim();
      origin = raw ? trimTrailingSlashes(raw) : undefined;
    }
  } catch {
    // ignore
  }
  if (origin) return `${origin}/telemetry/client-error`;
  return '/telemetry/client-error';
}

function windowErrorMessageLabel(message: string | Event): string {
  if (typeof message === 'string') return message;
  if (message instanceof Event) return `[Event:${message.type}]`;
  return unknownToLogString(message, 400);
}

/** Safe string summary of a rejection reason for telemetry (no raw object dumps). */
function rejectionReasonSummary(reason: unknown): string {
  if (reason instanceof Error) return reason.message.slice(0, 500);
  const sanitized = sanitizeForClientTelemetry(reason);
  try {
    return JSON.stringify(sanitized).slice(0, 500);
  } catch {
    return '[Unserializable rejection reason]';
  }
}

function posthogCapturePayload(props: Record<string, unknown>): Record<string, unknown> {
  return sanitizeForClientTelemetry(props) as Record<string, unknown>;
}

function emitServerTelemetry(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const endpoint = getTelemetryEndpoint();
  const sanitized = sanitizeForClientTelemetry(payload) as Record<string, unknown>;
  const body = JSON.stringify(sanitized);

  try {
    if (typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
      return;
    }
  } catch {
    // Fall back to fetch
  }

  void fetch(endpoint, { body, method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {
    // Avoid recursive error paths
  });
}

function errorForPosthogCapture(original: Error): Error {
  if (allowRawStacksInClientTelemetry()) return original;
  const e = new Error(original.message);
  e.name = original.name;
  return e;
}

/**
 * Handle window.onerror events
 */
function handleWindowError(
  message: string | Event,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
): void {
  const errorObj = error || new Error(typeof message === 'string' ? message : 'Unknown error');

  logger.error('[Global Error Handler] window.onerror:', errorObj, {
    message: windowErrorMessageLabel(message),
    source: source || 'unknown',
    lineno: lineno || 0,
    colno: colno || 0,
    errorType: 'window.onerror',
  });

  try {
    const posthog = getPostHogInstance();
    if (posthog) {
      const props: Record<string, unknown> = {
        errorType: 'window.onerror',
        source: source ?? undefined,
        lineno: lineno ?? undefined,
        colno: colno ?? undefined,
        message: windowErrorMessageLabel(message),
        error_message: errorObj.message,
      };
      if (allowRawStacksInClientTelemetry()) {
        props.stack = errorObj.stack;
      } else if (summarizeStack(errorObj.stack)) {
        props.stack_summary = summarizeStack(errorObj.stack);
      }
      const safeProps = posthogCapturePayload(props);
      if (typeof posthog.captureException === 'function') {
        posthog.captureException(errorForPosthogCapture(errorObj), safeProps);
      }
      if (typeof posthog.capture === 'function') {
        posthog.capture('client_error', safeProps);
      }
    }
  } catch (captureError) {
    logger.error('[PostHog] Failed to capture window.onerror', captureError, {
      module: 'errorHandling',
      action: 'capture_window_error',
    });
  }

  const beacon: Record<string, unknown> = {
    lineno,
    colno,
    source: 'window.onerror',
    message: windowErrorMessageLabel(message),
    filename: source,
    error: errorObj.message,
    timestamp: new Date().toISOString(),
  };
  if (allowRawStacksInClientTelemetry()) {
    beacon.stack = errorObj.stack;
  } else if (summarizeStack(errorObj.stack)) {
    beacon.stack_summary = summarizeStack(errorObj.stack);
  }
  emitServerTelemetry(beacon);
}

/**
 * Handle unhandled promise rejections
 */
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason;
  const errorMessage =
    reason instanceof Error
      ? reason.message
      : typeof reason === 'string'
        ? reason.slice(0, 500)
        : rejectionReasonSummary(reason);
  const error = reason instanceof Error ? reason : new Error(errorMessage);

  logger.error('[Global Error Handler] Unhandled promise rejection:', error, {
    reason: rejectionReasonSummary(reason),
    errorType: 'unhandledrejection',
  });

  try {
    const posthog = getPostHogInstance();
    if (posthog) {
      const props: Record<string, unknown> = {
        errorType: 'unhandledrejection',
        reason: rejectionReasonSummary(reason),
        error_message: error.message,
      };
      if (allowRawStacksInClientTelemetry()) {
        props.stack = reason instanceof Error ? reason.stack : undefined;
      } else if (reason instanceof Error && summarizeStack(reason.stack)) {
        props.stack_summary = summarizeStack(reason.stack);
      }
      const safeProps = posthogCapturePayload(props);
      if (typeof posthog.captureException === 'function') {
        posthog.captureException(errorForPosthogCapture(error), safeProps);
      }
      if (typeof posthog.capture === 'function') {
        posthog.capture('client_error', safeProps);
      }
    }
  } catch (captureError) {
    logger.error('[PostHog] Failed to capture unhandled rejection', captureError, {
      module: 'errorHandling',
      action: 'capture_unhandled_rejection',
    });
  }

  const beacon: Record<string, unknown> = {
    source: 'unhandledrejection',
    reason: rejectionReasonSummary(reason),
    timestamp: new Date().toISOString(),
  };
  if (allowRawStacksInClientTelemetry()) {
    beacon.stack = reason instanceof Error ? reason.stack : undefined;
  } else if (reason instanceof Error && summarizeStack(reason.stack)) {
    beacon.stack_summary = summarizeStack(reason.stack);
  }
  emitServerTelemetry(beacon);
}

/**
 * Initialize global error handlers
 * Should be called once during application startup
 */
export function initializeGlobalErrorHandlers(): void {
  if (isInitialized) {
    logger.warn('[Error Handling] Global error handlers already initialized', {
      module: 'errorHandling',
      action: 'initialize_handlers',
    });
    return;
  }

  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('error', (event) => {
    handleWindowError(
      event.message,
      event.filename || event.target?.toString(),
      event.lineno,
      event.colno,
      event.error
    );
  });

  window.addEventListener('unhandledrejection', (event) => {
    handleUnhandledRejection(event);
  });

  isInitialized = true;

  if (import.meta.env.DEV) {
    logger.info('[Error Handling] Global error handlers initialized', {
      module: 'errorHandling',
      action: 'initialize_handlers',
    });
  }
}

/**
 * Check if global error handlers are initialized
 */
export function areGlobalErrorHandlersInitialized(): boolean {
  return isInitialized;
}
